import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchDocumentTypes, deleteDocumentType } from './api';
import { DocumentTypeRead } from './types';
import {
  AddOutlined as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  CategoryOutlined as TypeIcon,
} from '@mui/icons-material';

const DocumentTypesList: React.FC = () => {
  const navigate = useNavigate();
  const [types, setTypes] = useState<DocumentTypeRead[]>([]);
  const [loading, setLoading] = useState(true);

  const loadTypes = async () => {
    setLoading(true);
    try {
      const data = await fetchDocumentTypes();
      setTypes(data);
    } catch (error) {
      console.error(error);
      alert('Ошибка загрузки типов документов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
  }, []);

  const handleDelete = async (id: number, name: string) => {
    if (window.confirm(`Вы уверены, что хотите перенести тип "${name}" в архив? Удаление может быть заблокировано, если к нему привязаны шаблоны.`)) {
      try {
        await deleteDocumentType(id);
        await loadTypes();
      } catch (error: any) {
        alert(`Ошибка при удалении: ${error.message}`);
      }
    }
  };

  return (
    <div className="w-full bg-white pb-10 min-h-screen">
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8 space-y-6 max-w-7xl mx-auto">

        {/* Заголовок */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <TypeIcon fontSize="large" className="text-[#1E61AF]" />
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Типы документов</h1>
              <p className="text-sm text-gray-500 mt-1">Управление системными типами (категориями) документов</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/documents/types/new')}
            className="flex items-center gap-2 bg-[#1E61AF] text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors font-medium text-sm shadow-sm"
          >
            <AddIcon fontSize="small" />
            <span>Добавить тип</span>
          </button>
        </div>

        {/* Таблица */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Название</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Код</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Описание</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                      Загрузка...
                    </td>
                  </tr>
                ) : types.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                      Типы документов не найдены
                    </td>
                  </tr>
                ) : (
                  types.map((type) => (
                    <tr key={type.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {type.name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className="font-mono text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                          {type.code}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 max-w-xs truncate" title={type.description || ''}>
                        {type.description || '—'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {type.is_active ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Активен
                          </span>
                        ) : (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Архив
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-1">
                          {/* Передача type через state роутера, чтобы не делать лишний запрос к API на странице редактирования */}
                          <button
                            onClick={() => navigate(`/documents/types/edit/${type.id}`, { state: { type } })}
                            className="p-2 text-gray-400 hover:text-blue-600 rounded-full hover:bg-gray-100 transition"
                            title="Редактировать"
                          >
                            <EditIcon sx={{ fontSize: 20 }} />
                          </button>
                          <button
                            onClick={() => handleDelete(type.id, type.name)}
                            disabled={!type.is_active}
                            className={`p-2 rounded-full transition ${
                              type.is_active
                                ? 'text-gray-400 hover:text-red-600 hover:bg-gray-100'
                                : 'text-gray-200 cursor-not-allowed'
                            }`}
                            title={type.is_active ? "В архив" : "Уже в архиве"}
                          >
                            <DeleteIcon sx={{ fontSize: 20 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DocumentTypesList;